import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import DeviceLabelPanel from "../components/DeviceLabelPanel";
import UserManualPanel from "../components/UserManualPanel";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { getApplicableProjectRole } from "../lib/roleWorkspace";
import { CaretLeft, FloppyDisk, CheckCircle, Cpu, FilePdf, Tag, Trash } from "@phosphor-icons/react";

const STATUSES = ["development", "technical_file", "nb_review", "certified", "on_market", "suspended", "withdrawn", "end_of_life"];
const CLASSES = ["", "I", "Is", "Im", "Ir", "IIa", "IIb", "III"];
const EUDAMED = ["not_started", "draft", "submitted", "registered"];

function normalizedVariants(value) {
  return (Array.isArray(value) ? value : []).map((item) => {
    if (typeof item === "string") return { name: item, model: "", udi_di: "", accessories: [] };
    if (!item || typeof item !== "object") return { name: "", model: "", udi_di: "", accessories: [] };
    return {
      ...item,
      name: item.name || item.model || item.label || item.variant || "",
      model: item.model || "",
      udi_di: item.udi_di || item.udiDi || "",
      accessories: Array.isArray(item.accessories)
        ? item.accessories
        : String(item.accessories || "").split("\n").map((entry) => entry.trim()).filter(Boolean),
    };
  });
}

const EMPTY = {
  name: "", model: "", device_type: "hardware", variants: [],
  emdn_code: "", emdn_description: "", gmdn_code: "",
  basic_udi_di: "", udi_di: "", udi_pi_format: "", issuing_entity: "GS1",
  mdr_class: "", classification_rule: "", classification_source: "manual",
  regulatory_pathway: "MDR", regulatory_status: "development",
  intended_purpose: "", indications: "", contraindications: "", intended_users: "",
  sterile: false, measuring_function: false, reusable_surgical: false,
  eudamed_registration_status: "not_started", eudamed_di_number: "",
  prrc: {}, notified_body: {}, ce_marking: {}, primary_project_id: "",
  observations: "",
  first_placed_on_market: "", next_psur_due: "", next_pms_review: "",
};

const ST_STYLE = {
  development: "bg-zinc-50 text-zinc-600 border-zinc-200",
  technical_file: "bg-blue-50 text-blue-800 border-blue-200",
  nb_review: "bg-amber-50 text-amber-800 border-amber-200",
  certified: "bg-emerald-50 text-emerald-800 border-emerald-200",
  on_market: "bg-emerald-50 text-emerald-800 border-emerald-200",
  suspended: "bg-red-50 text-red-700 border-red-200",
  withdrawn: "bg-red-50 text-red-700 border-red-200",
  end_of_life: "bg-zinc-100 text-zinc-500 border-zinc-300",
};

export default function DeviceDetail({ createNew = false }) {
  const { did, pid } = useParams();
  const isNew = createNew;
  const nav = useNavigate();
  const t = useT();
  const [d, setD] = useState(EMPTY);
  const [projects, setProjects] = useState([]);
  const [parentProject, setParentProject] = useState(null);
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (isNew) {
      if (!pid) {
        nav("/dashboard", { replace: true });
        return;
      }
      const { data: project } = await api.get(`/projects/${pid}`);
      setParentProject(project);
      setD({ ...EMPTY, primary_project_id: pid });
      return;
    }
    const [ps, dev] = await Promise.all([
      api.get("/projects"),
      api.get(`/devices/${did}`),
    ]);
    setProjects(ps.data || []);
    setD({ ...EMPTY, ...dev.data });
    setParentProject(null);
  }, [did, isNew, nav, pid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
  }, []);

  const set = (k, v) => setD((prev) => ({ ...prev, [k]: v }));

  async function save() {
    if (!d.name.trim()) return;
    setSaving(true);
    setMsg(null);
    try {
      const payload = {};
      Object.keys(EMPTY).forEach((k) => { payload[k] = d[k]; });
      if (isNew) {
        await api.post(`/projects/${pid}/devices`, payload);
        nav(`/projects/${pid}`, { replace: true });
      } else {
        await api.put(`/devices/${did}`, payload);
      }
      setMsg({ ok: true, text: t("dev.saved") });
    } catch (e) {
      setMsg({ ok: false, text: formatApiError(e.response?.data?.detail) });
    } finally { setSaving(false); }
  }

  async function remove() {
    if (!window.confirm(t("qms.confirm_delete"))) return;
    await api.delete(`/devices/${did}`);
    nav(linkedProject ? `/projects/${linkedProject.id}` : "/dashboard");
  }

  const linkedProject = parentProject || projects.find((p) => p.id === d.primary_project_id || p.device_id === d.id);
  const applicableRole = getApplicableProjectRole(linkedProject?.operating_role_view || linkedProject?.economic_operator_role);
  const variantRows = normalizedVariants(d.variants);
  const canEditVariants = applicableRole === "manufacturer";
  const showProductOverview = !isNew && Boolean(linkedProject) && !editing;

  useEffect(() => {
    if (isNew || !linkedProject?.id || !d.id) {
      setActiveAssessment(null);
      return undefined;
    }
    let cancelled = false;
    api.get(`/projects/${linkedProject.id}/versions`, { params: { device_id: d.id } })
      .then(({ data }) => {
        if (!cancelled) setActiveAssessment(data?.[0] || null);
      })
      .catch(() => {
        if (!cancelled) setActiveAssessment(null);
      });
    return () => { cancelled = true; };
  }, [isNew, linkedProject?.id, d.id]);

  function updateVariant(index, key, value) {
    const next = normalizedVariants(d.variants);
    next[index] = { ...next[index], [key]: value };
    set("variants", next);
  }

  function addVariant() {
    set("variants", [...variantRows, { name: "", model: "", udi_di: "", accessories: [] }]);
  }

  if (showProductOverview) {
    const classification = activeAssessment?.classification || {};
    const suggestedClass = classification.suggested_class || d.mdr_class || "";
    const matchedRule = (classification.rule_triggers || []).find(
      (trigger) => trigger?.matched !== false && String(trigger.result || "") === String(suggestedClass),
    );
    const classificationRule = matchedRule?.rule || d.classification_rule;
    const purpose = d.intended_purpose
      || activeAssessment?.intake?.intended_purpose?.description
      || activeAssessment?.intake?.intended_purpose
      || "";
    const roleSummaryKey = {
      manufacturer: "dev.overview.role_summary.manufacturer",
      importer: "dev.overview.role_summary.importer",
      distributor: "dev.overview.role_summary.distributor",
    }[applicableRole];

    return (
      <AppShell>
        <div className="mb-2">
          <Link to={`/projects/${linkedProject.id}`} className="inline-flex items-center gap-1 text-xs font-mono text-zinc-500 hover:text-blue-700 transition-colors" data-testid="device-back">
            <CaretLeft size={12} /> {t("pd.back")}
          </Link>
        </div>
        <section className="mx-auto max-w-3xl space-y-5" data-testid="device-product-overview">
          <header className="flex flex-col justify-between gap-3 border-b border-[var(--border)] pb-4 sm:flex-row sm:items-start">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-2)]">{t("dev.overview.eyebrow")} · {linkedProject.name}</div>
              <h1 className="mt-1 break-words text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="device-name-title">{d.name || t("dev.new_title")}</h1>
              {d.model && <p className="mt-1 text-sm text-[var(--text-2)]">{d.model}</p>}
            </div>
            <button type="button" onClick={() => setEditing(true)} className="btn-primary inline-flex shrink-0 items-center gap-2 px-4 py-2 text-sm" data-testid="device-edit-open">
              {t("dev.overview.edit")}
            </button>
          </header>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid-card p-4" data-testid="device-overview-classification">
              <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("classification.suggested")}</div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {suggestedClass
                  ? <span className={`chip chip-${suggestedClass} text-sm`} data-testid="device-overview-class">{suggestedClass}</span>
                  : <span className="text-sm text-[var(--text-2)]">{t("dev.overview.not_classified")}</span>}
                {classificationRule && (
                  <span className="text-xs text-[var(--text-2)]">{t("classification.rule")} {classificationRule}</span>
                )}
              </div>
              {purpose && <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-[var(--text-2)]">{purpose}</p>}
            </div>

            <div className="grid-card p-4" data-testid="device-overview-operator">
              <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("project.operational_role")}</div>
              <div className="mt-2 text-base font-bold text-[var(--text)]">
                {applicableRole ? t(`role.${applicableRole}`) : t("project.select_role")}
              </div>
              {roleSummaryKey && <p className="mt-2 text-sm leading-relaxed text-[var(--text-2)]">{t(roleSummaryKey)}</p>}
            </div>
          </div>

          {activeAssessment?.version && (
            <p className="text-xs text-[var(--text-2)]" data-testid="device-overview-assessment-version">
              {t("dev.overview.analysis_version")} {activeAssessment.version}
            </p>
          )}
        </section>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-2">
        <Link to={isNew ? `/projects/${pid}` : linkedProject ? `/projects/${linkedProject.id}` : "/dashboard"} className="inline-flex items-center gap-1 text-xs font-mono text-zinc-500 hover:text-blue-700 transition-colors" data-testid="device-back">
          <CaretLeft size={12} /> {t("pd.back")}
        </Link>
      </div>
      <div className="flex flex-col items-start justify-between gap-4 mb-6 sm:flex-row">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 inline-flex items-center gap-1">
            <Cpu size={12} /> {t("dev.device")} {!isNew && `· ${(d.id || "").slice(0, 8)}`}
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight break-words" data-testid="device-name-title">{d.name || t("dev.new_title")}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {d.mdr_class && <span className={`chip chip-${d.mdr_class}`} data-testid="device-class-chip">{t("dev.class")} {d.mdr_class}</span>}
            <span className={`inline-block border rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ST_STYLE[d.regulatory_status]}`} data-testid="device-status-chip">
              {t(`dev.st.${d.regulatory_status}`)}
            </span>
            {d.psur_periodicity && d.psur_periodicity !== "none" && (
              <span className="text-[10px] font-mono text-zinc-500">PSUR: {t(`dev.psur.${d.psur_periodicity}`)}</span>
            )}
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          {applicableRole === "manufacturer" && !isNew && linkedProject && (
            <button
              type="button"
              onClick={() => document.getElementById("user-manual-generator")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100"
              data-testid="device-user-manual-open"
            >
              <FilePdf size={14} /> {t("manual.open")}
            </button>
          )}
          {applicableRole === "manufacturer" && !isNew && linkedProject && (
            <button
              type="button"
              onClick={() => document.getElementById("device-label-generator")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="inline-flex items-center gap-1 border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-900 hover:bg-cyan-100"
              data-testid="device-label-open"
            >
              <Tag size={14} /> {t("labels.open")}
            </button>
          )}
          {!isNew && (
            <button onClick={remove} data-testid="device-delete" className="border border-red-200 text-red-600 hover:bg-red-50 px-3 py-2 rounded-sm text-xs font-semibold transition-colors">
              <Trash size={14} weight="bold" />
            </button>
          )}
          <button onClick={save} disabled={saving || !d.name.trim()} data-testid="device-save" className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-40">
            <FloppyDisk size={14} weight="bold" /> {saving ? "…" : t("qms.save")}
          </button>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 inline-flex items-center gap-1 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="device-msg">
          {msg.ok && <CheckCircle size={14} weight="fill" />} {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title={t("dev.sec_id")}>
          <F l={t("dev.name")} v={d.name} s={(v) => set("name", v)} tid="dev-f-name" />
          <F l={t("dev.model")} v={d.model} s={(v) => set("model", v)} tid="dev-f-model" />
          <Sel l={t("dev.type")} v={d.device_type} s={(v) => set("device_type", v)} tid="dev-f-type"
            opts={[["hardware", "Hardware"], ["software", "Software (SaMD)"], ["combined", t("dev.type_combined")]]} />
          <F l="EMDN" v={d.emdn_code} s={(v) => set("emdn_code", v)} tid="dev-f-emdn" />
          <F l={t("dev.emdn_desc")} v={d.emdn_description} s={(v) => set("emdn_description", v)} tid="dev-f-emdn-desc" />
          <F l="GMDN" v={d.gmdn_code} s={(v) => set("gmdn_code", v)} tid="dev-f-gmdn" />
        </Card>

        <Card title="UDI">
          <F l="Basic UDI-DI" v={d.basic_udi_di} s={(v) => set("basic_udi_di", v)} tid="dev-f-basic-udi" />
          <F l="UDI-DI" v={d.udi_di} s={(v) => set("udi_di", v)} tid="dev-f-udi-di" />
          <F l={t("dev.udi_pi_format")} v={d.udi_pi_format} s={(v) => set("udi_pi_format", v)} tid="dev-f-udi-pi" />
          <Sel l={t("dev.issuing")} v={d.issuing_entity} s={(v) => set("issuing_entity", v)} tid="dev-f-issuing"
            opts={[["GS1", "GS1"], ["HIBCC", "HIBCC"], ["ICCBBA", "ICCBBA"], ["IFA", "IFA"]]} />
          <Sel l="EUDAMED" v={d.eudamed_registration_status} s={(v) => set("eudamed_registration_status", v)} tid="dev-f-eudamed"
            opts={EUDAMED.map((e) => [e, t(`dev.eud.${e}`)])} />
          <F l={t("dev.eudamed_di")} v={d.eudamed_di_number} s={(v) => set("eudamed_di_number", v)} tid="dev-f-eudamed-di" />
          <div className="col-span-2 space-y-3 rounded-sm border border-[var(--border)] bg-[var(--surface)] p-3" data-testid="device-variant-udi-editor">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-semibold text-[var(--text)]">{t("dev.variants")}</div>
                <div className="mt-1 text-[11px] text-[var(--text-2)]">{t("dev.variant_edit_note")}</div>
              </div>
              {canEditVariants && (
                <button type="button" onClick={addVariant}
                  className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)]"
                  data-testid="device-add-variant">
                  {t("dev.add_variant")}
                </button>
              )}
            </div>
            {variantRows.length === 0 ? (
              <p className="text-xs text-[var(--text-2)]">{t("dev.no_variants")}</p>
            ) : (
              <div className="space-y-2">
                {variantRows.map((variant, index) => (
                  <div key={`${index}-${variant.name}`} className="grid grid-cols-1 gap-3 rounded-sm border border-[var(--border)] p-3 sm:grid-cols-2">
                    <label className="block min-w-0">
                      <span className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-[var(--text-2)]">{t("dev.variant_name")}</span>
                      <input value={variant.name || ""} readOnly={!canEditVariants}
                        onChange={(event) => updateVariant(index, "name", event.target.value)}
                        data-testid={`device-variant-name-${index}`}
                        className="w-full border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--text)] focus:border-blue-600 focus:outline-none" />
                    </label>
                    <label className="block min-w-0">
                      <span className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-[var(--text-2)]">{t("dev.variant_model")}</span>
                      <input value={variant.model || ""} readOnly={!canEditVariants}
                        onChange={(event) => updateVariant(index, "model", event.target.value)}
                        data-testid={`device-variant-model-${index}`}
                        className="w-full border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--text)] focus:border-blue-600 focus:outline-none" />
                    </label>
                    <label className="block min-w-0">
                      <span className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-[var(--text-2)]">{t("dev.variant_udi_di")}</span>
                      <input value={variant.udi_di || ""} readOnly={!canEditVariants}
                        onChange={(event) => updateVariant(index, "udi_di", event.target.value)}
                        data-testid={`device-variant-udi-${index}`}
                        className="w-full border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--text)] focus:border-blue-600 focus:outline-none" />
                    </label>
                    <label className="block min-w-0 sm:col-span-2">
                      <span className="mb-1 block text-[10px] font-mono uppercase tracking-wider text-[var(--text-2)]">{t("dev.variant_accessories")}</span>
                      <textarea
                        value={(variant.accessories || []).join("\n")}
                        readOnly={!canEditVariants}
                        onChange={(event) => updateVariant(index, "accessories", event.target.value.split("\n").map((entry) => entry.trim()).filter(Boolean))}
                        rows={2}
                        placeholder={t("dev.variant_accessories_hint")}
                        data-testid={`device-variant-accessories-${index}`}
                        className="w-full resize-y border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--text)] focus:border-blue-600 focus:outline-none"
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <Card title={t("dev.sec_reg")}>
          <Sel l={t("dev.class")} v={d.mdr_class} s={(v) => set("mdr_class", v)} tid="dev-f-class"
            opts={CLASSES.map((c) => [c, c || "—"])} />
          <F l={t("dev.rule")} v={d.classification_rule} s={(v) => set("classification_rule", v)} tid="dev-f-rule" />
          <Sel l={t("dev.pathway")} v={d.regulatory_pathway} s={(v) => set("regulatory_pathway", v)} tid="dev-f-pathway"
            opts={[["MDR", "MDR 2017/745"], ["legacy_MDD_transition", t("dev.legacy")]]} />
          <Sel l={t("dev.status")} v={d.regulatory_status} s={(v) => set("regulatory_status", v)} tid="dev-f-status"
            opts={STATUSES.map((s) => [s, t(`dev.st.${s}`)])} />
          <div className="col-span-2">
            <A l={t("dev.purpose")} v={d.intended_purpose} s={(v) => set("intended_purpose", v)} tid="dev-f-purpose" />
          </div>
          <A l={t("dev.indications")} v={d.indications} s={(v) => set("indications", v)} tid="dev-f-indications" />
          <A l={t("dev.contraindications")} v={d.contraindications} s={(v) => set("contraindications", v)} tid="dev-f-contra" />
          <div className="col-span-2 flex flex-wrap gap-4 pt-1">
            {[["sterile", t("dev.sterile")], ["measuring_function", t("dev.measuring")], ["reusable_surgical", t("dev.reusable")]].map(([k, l]) => (
              <label key={k} className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-700">
                <input type="checkbox" checked={!!d[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`dev-f-${k}`} /> {l}
              </label>
            ))}
          </div>
        </Card>

        <Card title={t("dev.sec_links")}>
          <div className="col-span-2">
            <label className="text-xs font-semibold uppercase text-zinc-600">{t("dev.linked_project")}</label>
            {isNew ? (
              <div className="mt-1 border border-zinc-200 bg-zinc-50 px-3 py-2 rounded-sm text-sm" data-testid="dev-f-project">
                {parentProject?.name || "—"}
              </div>
            ) : (
              <select value={d.primary_project_id || ""} onChange={(e) => set("primary_project_id", e.target.value)} data-testid="dev-f-project"
                className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
                <option value="">—</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.device_name})</option>)}
              </select>
            )}
            {linkedProject && !isNew && (
              <Link to={`/projects/${linkedProject.id}?section=overview&device_id=${encodeURIComponent(d.id)}`} data-testid="dev-open-project"
                className="inline-block mt-2 text-xs font-bold text-blue-700 hover:underline">
                {t("dev.open_project")} →
              </Link>
            )}
          </div>
          <F l={`PRRC — ${t("dev.prrc_name")}`} v={d.prrc?.name || ""} s={(v) => set("prrc", { ...d.prrc, name: v })} tid="dev-f-prrc-name" />
          <F l={t("dev.prrc_qual")} v={d.prrc?.qualification || ""} s={(v) => set("prrc", { ...d.prrc, qualification: v })} tid="dev-f-prrc-qual" />
          <F l={`${t("dev.nb")} — Nº`} v={d.notified_body?.number || ""} s={(v) => set("notified_body", { ...d.notified_body, number: v })} tid="dev-f-nb-number" />
          <F l={`${t("dev.nb")} — ${t("dev.name")}`} v={d.notified_body?.name || ""} s={(v) => set("notified_body", { ...d.notified_body, name: v })} tid="dev-f-nb-name" />
          <F l={t("dev.placed")} type="date" v={d.first_placed_on_market} s={(v) => set("first_placed_on_market", v)} tid="dev-f-placed" />
          <F l={t("dev.next_psur")} type="date" v={d.next_psur_due} s={(v) => set("next_psur_due", v)} tid="dev-f-next-psur" />
          <F l={t("dev.next_pms")} type="date" v={d.next_pms_review} s={(v) => set("next_pms_review", v)} tid="dev-f-next-pms" />
        </Card>

        <Card title={t("dev.observations_section")}>
          <div className="col-span-2">
            <A
              l={t("dev.observations")}
              v={d.observations}
              s={(v) => set("observations", v)}
              tid="dev-f-observations"
            />
          </div>
        </Card>
      </div>
      {applicableRole === "manufacturer" && !isNew && linkedProject && <UserManualPanel device={d} projectId={linkedProject.id} />}
      {applicableRole === "manufacturer" && !isNew && linkedProject && <DeviceLabelPanel device={d} />}
    </AppShell>
  );
}

function Card({ title, children }) {
  return (
    <div className="grid-card p-5">
      <div className="text-xs font-mono uppercase tracking-widest text-blue-800 font-bold mb-4">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function F({ l, v, s, type = "text", tid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{l}</label>
      <input type={type} value={v || ""} onChange={(e) => s(e.target.value)} data-testid={tid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}

function A({ l, v, s, tid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{l}</label>
      <textarea rows={2} value={v || ""} onChange={(e) => s(e.target.value)} data-testid={tid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}

function Sel({ l, v, s, opts, tid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{l}</label>
      <select value={v || ""} onChange={(e) => s(e.target.value)} data-testid={tid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
        {opts.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
      </select>
    </div>
  );
}
